import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { AuthService } from './Services/auth.service';

// To prevent multiple refresh calls at once
let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
  null
);

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const token = authService.getAccessToken();

  // 1. Identify if this is the refresh request
  const isRefreshRequest = req.url.includes('auth/access-token');

  // 1. Attach Token
  let authReq = req;
  if (token && !isRefreshRequest) {
    authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  // 2. Handle Response & Errors
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // 2. If the REFRESH call failed with 401, kill the session immediately
        if (isRefreshRequest) {
          authService.logout();
          router.navigate(['/login']);
          return throwError(() => new Error('Refresh token expired'));
        }
        // 3. Otherwise, try the silent refresh
        return handle401Error(req, next, authService, router);
      }

      console.error(error);
      return throwError(() => error);
    })
  );
};

// --- Helper Function for 401s ---
function handle401Error(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshAccessToken().pipe(
      switchMap((token: any) => {
        isRefreshing = false;
        refreshTokenSubject.next(token);

        // Retry the failed request with the NEW token
        return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
      }),
      catchError((err) => {
        isRefreshing = false;
        // REFRESH TOKEN ALSO EXPIRED - Logout user
        authService.logout();
        router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  } else {
    // If a refresh is already in progress, wait for the new token
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })))
    );
  }
}
